import { FormEvent, useEffect, useState } from 'react';
import Money from './Money';
import InfoTip from './InfoTip';
import { deliveryConfigApi, formIssues, issueFor, toValidationIssues, ValidationIssue } from '../../services/deliveryConfigApi';
import { DeliveryFeeConfig, splitVatInclusive } from '../../types/delivery';

interface DeliveryFeeFormProps {
  config: DeliveryFeeConfig;
  token: string;
  /** Called with the saved config so the tab can re-render the "in force" figures. */
  onSaved: (config: DeliveryFeeConfig) => void;
}

export function FieldError({ message, id }: { message: string | undefined; id: string }) {
  if (!message) return null;
  return (
    <div id={id} role="alert" style={{ fontSize: '0.75rem', color: 'var(--admin-danger)', marginTop: '0.25rem' }}>
      {message}
    </div>
  );
}

/** Last 8 characters, the same shortening the offering history tab uses for an unresolved actor. */
function shortSubjectId(subjectId: string): string {
  return subjectId.length > 8 ? subjectId.slice(-8) : subjectId;
}

/**
 * #554 — who last changed the fee, in words. The raw subject id identifies the actor to the
 * database and to nobody else, so prefer the name identity resolved and keep the short id only as
 * the fallback for an account it can no longer name: a deleted admin still belongs in the trail.
 * Mirrors `actorName` in `components/services/HistoryTab.tsx`. Spec 072 v2: the Zofeur card reuses it.
 */
export function actorLabel(config: Pick<DeliveryFeeConfig, 'updatedBy' | 'updatedByName'>): string | null {
  const name = config.updatedByName?.trim();
  if (name) return name;
  return config.updatedBy ? `Admin ${shortSubjectId(config.updatedBy)}` : null;
}

function parseAmount(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/**
 * Spec 053 Flow F6 — the delivery-fee half of Finance → Configuration.
 *
 * Two numbers only: the threshold the VAT-inclusive service price is compared against (inclusive),
 * and Garagat's own fee per Garagat-operated leg, VAT-inclusive — the figure the **customer** pays.
 * Both are snapshotted onto a booking at booking time (BR-21), so a change here reaches new
 * bookings only; the form says so on screen rather than leaving an admin to discover it.
 */
export default function DeliveryFeeForm({ config, token, onSaved }: DeliveryFeeFormProps) {
  const [threshold, setThreshold] = useState(() => String(config.threshold));
  const [feePerTrip, setFeePerTrip] = useState(() => String(config.feePerTrip));
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setThreshold(String(config.threshold));
    setFeePerTrip(String(config.feePerTrip));
  }, [config]);

  const split = splitVatInclusive(config.feePerTrip);
  const actor = actorLabel(config);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaved(false);
    setError(null);

    const parsedThreshold = parseAmount(threshold);
    const parsedFee = parseAmount(feePerTrip);
    const local: ValidationIssue[] = [];
    if (parsedThreshold === null || parsedThreshold < 0) {
      local.push({ field: 'threshold', message: 'Enter the threshold as a positive amount in AED' });
    }
    if (parsedFee === null || parsedFee < 0) {
      local.push({ field: 'feePerTrip', message: 'Enter the fee per trip as a positive amount in AED' });
    }
    if (local.length > 0) { setIssues(local); return; }

    setIssues([]);
    setSaving(true);
    try {
      const updated = await deliveryConfigApi.updateConfig(
        { threshold: parsedThreshold as number, feePerTrip: parsedFee as number },
        token,
      );
      setSaved(true);
      onSaved(updated);
    } catch (err) {
      const parsed = toValidationIssues(err);
      if (parsed.length > 0) setIssues(parsed);
      else setError(err instanceof Error ? err.message : 'Failed to save the delivery fee');
    } finally {
      setSaving(false);
    }
  };

  const formLevel = formIssues(issues);

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Pickup &amp; delivery fee</h3>
        {config.updatedAt && (
          <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }} data-testid="config-last-changed">
            Last changed {new Date(config.updatedAt).toLocaleString()}
            {actor ? ` by ${actor}` : ''}
          </span>
        )}
      </div>

      <div className="admin-info-banner info" role="note" data-testid="new-bookings-only">
        Changes apply to <strong>new bookings only</strong>. Every booking keeps the threshold and fee
        that were in force when it was made, so orders already placed are never repriced.
      </div>

      {error && <div className="admin-info-banner danger" role="alert" data-testid="config-error">{error}</div>}
      {formLevel.length > 0 && (
        <div className="admin-info-banner danger" role="alert" data-testid="config-form-error">
          {formLevel.join(' ')}
        </div>
      )}
      {saved && <div className="admin-info-banner success" role="status" data-testid="config-saved">Saved. New bookings from now on use these figures.</div>}

      {/* #1125: three across when there is room, stacked on a phone (fixed columns overlapped the figures). */}
      <div data-testid="delivery-fee-figures" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(10rem, 100%), 1fr))', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Threshold in force</div>
          <Money amount={config.threshold} glossaryKey="deliveryFeeThreshold" style={{ fontSize: '1.125rem', fontWeight: 600 }} />
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
            Charged at or below this price; free above it
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Fee per trip in force</div>
          <Money amount={config.feePerTrip} glossaryKey="deliveryFeePerTrip" style={{ fontSize: '1.125rem', fontWeight: 600 }} />
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
            What the customer pays per Garagat leg, incl. VAT
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Of which</div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {/*
              BR-31 — each figure carries its OWN ⓘ for its OWN key. These used to be `hideTip`
              behind a single icon bound to `deliveryFeePerTripNet`, which left the VAT figure with
              no adjacent icon at all (#554).
            */}
            <span style={{ fontSize: '0.8125rem' }}>
              net <Money amount={split.net} glossaryKey="deliveryFeePerTripNet" />
            </span>
            <span style={{ fontSize: '0.8125rem' }}>
              VAT <Money amount={split.vat} glossaryKey="deliveryFeePerTripVat" />
            </span>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
            Two Garagat legs invoice twice each of these
          </div>
        </div>
      </div>

      <form onSubmit={submit} aria-label="Delivery fee configuration" style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="admin-field" style={{ margin: 0 }}>
          <label className="admin-field-label" htmlFor="delivery-threshold">
            Threshold (AED, incl. VAT)
            <InfoTip glossaryKey="deliveryFeeThreshold" />
          </label>
          <input
            id="delivery-threshold"
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            className="admin-search-input"
            style={{ width: '12rem' }}
            value={threshold}
            disabled={saving}
            aria-invalid={!!issueFor(issues, 'threshold')}
            aria-describedby={issueFor(issues, 'threshold') ? 'delivery-threshold-error' : undefined}
            onChange={(e) => setThreshold(e.target.value)}
          />
          <FieldError id="delivery-threshold-error" message={issueFor(issues, 'threshold')} />
        </div>

        <div className="admin-field" style={{ margin: 0 }}>
          <label className="admin-field-label" htmlFor="delivery-fee-per-trip">
            Garagat fee per trip (AED, incl. VAT)
            <InfoTip glossaryKey="deliveryFeePerTrip" />
          </label>
          <input
            id="delivery-fee-per-trip"
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            className="admin-search-input"
            style={{ width: '14rem' }}
            value={feePerTrip}
            disabled={saving}
            aria-invalid={!!issueFor(issues, 'feePerTrip')}
            aria-describedby={issueFor(issues, 'feePerTrip') ? 'delivery-fee-per-trip-error' : undefined}
            onChange={(e) => setFeePerTrip(e.target.value)}
          />
          <FieldError id="delivery-fee-per-trip-error" message={issueFor(issues, 'feePerTrip')} />
        </div>

        <button type="submit" className="admin-btn admin-btn-sm admin-btn-primary" style={{ marginTop: '1.6rem' }} disabled={saving}>
          {saving ? 'Saving…' : 'Save fee settings'}
        </button>
      </form>
    </div>
  );
}
